// =============================================================================
// siteWalks.ts -- Patch 010.B.2 frontend API client for the Track 2 site-walks
// surface (backend shipped in 010.B.1). Mirrors the route layout in
// workers/core-os/src/routes/site_walks.ts.
//
// Photos and voice notes flow through the existing documents chassis
// (documents.upload returns a document_id; we attach those ids to
// site_walk_items.photo_document_ids / voice_note_audio_document_id via
// updateItem).
// =============================================================================

import { request } from './request'
import type {
  AddItemInput,
  ChecklistTemplateItem,
  SiteWalkAnalyzeResponse,
  SiteWalkDetailResponse,
  SiteWalkListEntry,
  StartWalkInput,
  UpdateItemInput,
  UpdateWalkInput,
} from './types'

export const siteWalksApi = {
  checklist: (projectId: string, phase: string) =>
    request<{ ok: true; phase: string; items: ChecklistTemplateItem[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/checklist?phase=${encodeURIComponent(phase)}`,
    ),

  list: (
    projectId: string,
    params?: { phase?: string; status?: 'open' | 'completed' | 'archived'; limit?: number },
  ) => {
    const q: string[] = []
    if (params?.phase) q.push(`phase=${encodeURIComponent(params.phase)}`)
    if (params?.status) q.push(`status=${encodeURIComponent(params.status)}`)
    if (params?.limit !== undefined) q.push(`limit=${params.limit}`)
    const qs = q.length > 0 ? `?${q.join('&')}` : ''
    return request<{ ok: true; walks: SiteWalkListEntry[]; count: number }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks${qs}`,
    )
  },

  get: (projectId: string, walkId: string) =>
    request<SiteWalkDetailResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}`,
    ),

  analyzeWalk: (projectId: string, walkId: string) =>
    request<SiteWalkAnalyzeResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/analyze`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // Patch 010.B.2.7 — promote an AI flag to a punch_items row. Critical /
  // warning flags only; the backend rejects info severity (400). Returns
  // the new punch item id.
  promoteFlag: (projectId: string, walkId: string, flagId: string) =>
    request<{ ok: true; flag_id: string; punch_item_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/flags/${encodeURIComponent(flagId)}/promote`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // Patch 010.B.2.8 — cross-walk progress diff. One Vision call comparing
  // this walk's photos against the most-recent prior walk's photos.
  detectProgress: (projectId: string, walkId: string) =>
    request<{
      ok: true
      id: string
      observations: string
      estimated_pct: number | null
      compared_walk_id: string
      compared_walked_at: string
      generated_at: string
      suggested_completion_count: number
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/detect-progress`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // Patch 010.B.2.8.1 — confirm a suggested task completion. Completes the
  // schedule_task and stamps the suggestion confirmed (the human-approved
  // crossing). Per-task only; there is no bulk control.
  confirmTaskSuggestion: (projectId: string, walkId: string, suggestionId: string) =>
    request<{
      ok: true
      suggestion_id: string
      schedule_task_id: string
      task_already_complete: boolean
      request_id: string
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/task-suggestions/${encodeURIComponent(suggestionId)}/confirm`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // Patch 010.B.2.8.1 — dismiss a suggested task completion. No schedule
  // write; the row drops from the embedded list.
  dismissTaskSuggestion: (projectId: string, walkId: string, suggestionId: string) =>
    request<{ ok: true; suggestion_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/task-suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  start: (projectId: string, body: StartWalkInput) =>
    request<{
      ok: true
      id: string
      phase: string
      walker_profile_id: string
      walked_at: string
      request_id: string
    }>(`/api/projects/${encodeURIComponent(projectId)}/site-walks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateWalk: (projectId: string, walkId: string, body: UpdateWalkInput) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  addItem: (projectId: string, walkId: string, body: AddItemInput) =>
    request<{ ok: true; id: string; walk_id: string; status: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/items`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updateItem: (
    projectId: string,
    walkId: string,
    itemId: string,
    body: UpdateItemInput,
  ) =>
    request<{ ok: true; id: string; updated: string[] }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/items/${encodeURIComponent(itemId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  // Patch 010.B.2.5 — walk-level documents. The file is uploaded through
  // api.documents.upload first; attachDocument links the returned
  // document_id to the walk. The list arrives embedded in get().
  attachDocument: (
    projectId: string,
    walkId: string,
    body: { document_id: string; kind: 'photo' | 'voice_note'; notes?: string | null },
  ) =>
    request<{ ok: true; id: string; walk_id: string; kind: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/documents`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  removeDocument: (projectId: string, walkId: string, linkId: string) =>
    request<{ ok: true; id: string; archived_at: string; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/documents/${encodeURIComponent(linkId)}`,
      { method: 'DELETE' },
    ),

  transcribeItem: (projectId: string, walkId: string, itemId: string) =>
    request<{
      ok: true
      item_id: string
      voice_note_text: string
      request_id: string
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/site-walks/${encodeURIComponent(walkId)}/items/${encodeURIComponent(itemId)}/transcribe`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
}

// Re-export types so consumers can pull from siteWalks barrel if preferred.
export type {
  SiteWalkRow,
  SiteWalkListEntry,
  SiteWalkDetailResponse,
  SiteWalkAnalyzeResponse,
} from './types'
